import React, { useState, useEffect, useRef } from "react";
import { Table, Col, Breadcrumb, Button, Space, Row, Spin } from "antd";
import { Link, useParams } from "react-router-dom";
import { DownloadOutlined } from "@ant-design/icons";
import plogo from "../../image/plogo.png";
import { getRequest } from "../../axios";
import PropTypes from "prop-types";
import ButtonField from "../../common/htmlComponents/ButtonField";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import newLogo from "../../image/PROCEM3.svg";
import {
  fetchGeneralPoPdfData,
  formatPoAddress,
} from "../generalProcurement/generalProcurementPoUtils";

const GeneralPurchaseOrder = () => {
  const [orderData, setOrderData] = useState(null);

  const { id, vendorId } = useParams();
  const buttonRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const pdfContainerRef = useRef(null);

  const units = [
    "",
    "ONE",
    "TWO",
    "THREE",
    "FOUR",
    "FIVE",
    "SIX",
    "SEVEN",
    "EIGHT",
    "NINE",
  ];
  const tens = [
    "",
    "TEN",
    "TWENTY",
    "THIRTY",
    "FORTY",
    "FIFTY",
    "SIXTY",
    "SEVENTY",
    "EIGHTY",
    "NINETY",
  ];
  const teens = [
    "TEN",
    "ELEVEN",
    "TWELVE",
    "THIRTEEN",
    "FOURTEEN",
    "FIFTEEN",
    "SIXTEEN",
    "SEVENTEEN",
    "EIGHTEEN",
    "NINETEEN",
  ];

  const zero = "ZERO";
  const arab = "ARAB";
  const crore = "CRORE";
  const lakh = "LAKH";
  const thousand = "THOUSAND";
  const hundred = "HUNDRED";
  const currency = "RUPEES";
  const paisa = "PAISA";
  const only = "ONLY";

  const convertNumberToWords = (amount) => {
    if (amount === 0) return `${currency} ${zero} ${only}`;

    function convert(num) {
      let parts = [];
      if (num >= 1e9) {
        parts.push(`${convert(Math.floor(num / 1e9))} ${arab}`);
        num %= 1e9;
      }
      if (num >= 1e7) {
        parts.push(`${convert(Math.floor(num / 1e7))} ${crore}`);
        num %= 1e7;
      }
      if (num >= 1e5) {
        parts.push(`${convert(Math.floor(num / 1e5))} ${lakh}`);
        num %= 1e5;
      }
      if (num >= 1000) {
        parts.push(`${convert(Math.floor(num / 1000))} ${thousand}`);
        num %= 1000;
      }
      if (num >= 100) {
        parts.push(`${convert(Math.floor(num / 100))} ${hundred}`);
        num %= 100;
      }
      if (num >= 20) {
        parts.push(`${tens[Math.floor(num / 10)]}`);
        if (num % 10 > 0) parts.push(units[num % 10]);
      } else if (num >= 10) {
        parts.push(`${teens[num - 10]}`);
      } else if (num > 0) {
        parts.push(`${units[num]}`);
      }
      return parts.join(" ");
    }

    let integerPart = Math.floor(amount);
    let wholeWordPart = convert(integerPart);
    let result = wholeWordPart ? `${currency} ${wholeWordPart} ` : "";

    let decimalPart = Math.round((amount - integerPart) * 100);
    if (decimalPart > 0) {
      if (wholeWordPart) {
        result += " AND ";
      }
      result += `${convert(decimalPart)} ${paisa}`;
    }

    return `${result} ${only}`;
  };
  useEffect(() => {
    let cancelled = false;

    const getPoOrder = async () => {
      try {
        setLoadError(null);
        const normalizedData = await fetchGeneralPoPdfData(
          id,
          vendorId,
          getRequest,
        );

        if (cancelled) {
          return;
        }

        if (!normalizedData) {
          setLoadError("Failed to load purchase order details.");
          setOrderData(null);
          return;
        }

        setOrderData(normalizedData);
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("Error:", error);
        setLoadError("Failed to load purchase order details.");
        setOrderData(null);
      }
    };
    getPoOrder();

    return () => {
      cancelled = true;
    };
  }, [id, vendorId]);

  const formatPoDate = (dateValue) => {
    if (!dateValue) return "";
    const parsed = moment(dateValue, [
      "DD-MM-YYYY",
      "DD-MM-YY",
      "YYYY-MM-DD",
      "DD/MM/YYYY",
      "DD/MM/YY",
    ]);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : dateValue;
  };

  if (loadError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        {loadError}
      </div>
    );
  }

  if (!orderData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  const getBase64ImageFromUrl = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      };
      img.onerror = (err) => {
        reject(err);
      };
    });
  };
  const downloadAsPDF = () => {
    if (pdfContainerRef.current) {
      setIsDownloading(true);

      // Hide download button during capture
      if (buttonRef.current) {
        buttonRef.current.classList.add("hide-in-pdf");
      }

      html2canvas(pdfContainerRef.current, {
        useCORS: true,
        allowTaint: false,
        scale: 2,
        logging: true,
      })
        .then((canvas) => {
          const imgData = canvas.toDataURL("image/png");

          // Optionally load a base64 image (not used here but in your original code)
          getBase64ImageFromUrl(
            "http://smartbuyer-dev-files.s3.ap-south-1.amazonaws.com/CompanyDocument/8_20241001_114449.png"
          )
            .then((base64Image) => {
              console.log("Base64 image loaded");
            })
            .catch((err) => console.error("Error loading image", err));

          const pdf = new jsPDF("p", "mm", "a4");
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

          const canvasAspectRatio = canvas.width / canvas.height;
          const pdfAspectRatio = pdfWidth / pdfHeight;

          let imgWidth, imgHeight;

          // Scale to fit width or height proportionally
          if (canvasAspectRatio > pdfAspectRatio) {
            imgWidth = pdfWidth;
            imgHeight = (canvas.height * pdfWidth) / canvas.width;
          } else {
            imgHeight = pdfHeight;
            imgWidth = (canvas.width * pdfHeight) / canvas.height;
          }

          // Align image at the top (no extra top space)
          const xOffset = (pdfWidth - imgWidth) / 2;
          const yOffset = 0;

          pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);

          // Naming the file
          let poNumber = orderData.po_number || "";
          let vendorName = orderData.vendor_details?.[0]?.name || "vendor";
          let clientName = orderData.company_details?.[0]?.name || "client";
          let poDate = orderData.po_created_date || "";

          const shortName = (name) =>
            name.replace(/\s+/g, "").toLowerCase().substring(0, 6);

          vendorName = shortName(vendorName);
          clientName = shortName(clientName);

          let formattedDate = "";
          if (poDate) {
            const m = moment(poDate, [
              "DD-MM-YYYY",
              "YYYY-MM-DD",
              "DD/MM/YYYY",
            ]);
            if (m.isValid()) {
              formattedDate = m.format("DD/MM/YY");
            }
          }

          const fileName = `PO_${poNumber}_${vendorName}_${clientName}_${formattedDate}_Priced.pdf`;
          pdf.save(fileName);

          if (buttonRef.current) {
            buttonRef.current.classList.remove("hide-in-pdf");
          }

          setIsDownloading(false);
        })
        .catch((error) => {
          console.error("Error generating PDF:", error);
          if (buttonRef.current) {
            buttonRef.current.classList.remove("hide-in-pdf");
          }
          setIsDownloading(false);
        });
    } else {
      console.error("PDF container reference is not defined.");
    }
  };

  let columns = [
    { title: "Sr.", dataIndex: "SrNo", width: "50px" },
    { title: "Item Description", dataIndex: "ItemDescription" },
  ];

  if (orderData.is_diameter_wise) {
    columns.push({
      title: "Diameter",
      dataIndex: "Diameter",
      align: "center",
      render: (text, record) =>
        record.Diameter
          ? `${record.Diameter} ${
              record.DiameterUOM ? record.DiameterUOM.toUpperCase() : ""
            }`
          : "",
    });
  }

  columns = columns.concat([
    { title: "Qty.", dataIndex: "QTY", align: "center" },
    {
      title: "UoM.",
      dataIndex: "UOM",
      align: "center",
      render: (text, record) => {
        if (text === "Cubic Meter") {
          return "Cu. Mtr.";
        } else {
          return text;
        }
      },
    },
    { title: "Delivery Date", dataIndex: "DeliveryDate" },
    { title: "Tax(%)", dataIndex: "Tax", align: "center" },
    { title: "Unit Rate", dataIndex: "UnitRate", align: "right" },
  ]);

  if (orderData.is_freight_excluded === true) {
    columns.push({
      title: "Unit Freight",
      dataIndex: "freight",
      align: "right",
    });
  }

  columns.push({
    title: "Basic Amount",
    dataIndex: "BasicAmount",
    align: "right",
  });

  let data = [];
  const rfqItems = orderData.rfq_item ?? [];

  if (orderData.is_diameter_wise) {
    let srNo = 1;
    rfqItems.forEach((item) => {
      (item.diameter_details ?? []).forEach((diameter, idx) => {
        data.push({
          SrNo: idx === 0 ? srNo : "",
          ItemDescription: idx === 0 ? item.material_name : "",
          Diameter: diameter.diameter_value,
          DiameterUOM: diameter.diameter_uom,
          QTY: diameter.diameter_quantity,
          UOM: item.uom,
          DeliveryDate: item.delivery_date,
          Tax: item.tax,
          UnitRate: "₹ " + diameter.basic_unit_price,
          freight: "₹ " + diameter.unit_rate_of_freight,
          BasicAmount:
            "₹ " +
            diameter.basic_amount.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            }),
        });
      });
      srNo++;
    });
  } else {
    data = rfqItems.map((item, index) => ({
      SrNo: index + 1,
      ItemDescription: item.material_name,
      QTY: item.qty,
      UOM: item.uom,
      DeliveryDate: item.delivery_date,
      Tax: item.tax,
      UnitRate: "₹ " + item.basic_unit_price,
      freight: "₹ " + item.unit_rate_of_freight,
      BasicAmount:
        "₹ " +
        Number(item.basic_amount || 0).toLocaleString("en-IN", {
          maximumFractionDigits: 0,
        }),
    }));
  }

  const company = orderData.company_details?.[0] ?? {};
  const vendor = orderData.vendor_details?.[0] ?? {};
  const siteLocation = orderData.site_location?.[0] ?? {};
  const billingDetails = siteLocation.billing_details ?? {};
  const shippingDetails = siteLocation.shipping_details ?? {};
  const totals = orderData.total?.[0] ?? {
    basic_sub_total: 0,
    cgst: 0,
    sgst: 0,
    total: 0,
  };

  return (
    <>
      <div className="invoice-container">
        <div className="ListingTopBar">
          <div className="pagenamewrap">
            <Space>
              <ButtonField
                className="BlueShadowBtn"
                type="primary"
                icon={<DownloadOutlined />}
                label={isDownloading ? "Downloading..." : "Download PDF"}
                onClick={downloadAsPDF}
                ref={buttonRef}
              >
                Download
              </ButtonField>
            </Space>
          </div>
        </div>

        <section className="grid-sec mt-5" ref={pdfContainerRef}>
          <div className="PR-body">
            <div
              style={{
                fontFamily: "Arial, sans-serif",
                fontSize: "13px",
                color: "#333",
                lineHeight: "0.9",
              }}
            >
              <h4
                style={{
                  marginLeft: "10px",
                  borderBottom: "1px solid #bcb6b6",
                }}
              >
                Purchase Order
              </h4>
              <div
                style={{ display: "flex", borderBottom: "1px solid #bcb6b6" }}
              >
                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>Buyer Details</h4>
                  <p>
                    <strong>Name:</strong> {company.name || "N/A"}
                  </p>
                  <p>
                    <strong>Mobile:</strong> {company.phone_number || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {company.email || "N/A"}
                  </p>
                  <p>
                    <strong>Address:</strong>{" "}
                    {formatPoAddress(
                      company.registered_business_address_building,
                      company.registered_business_address_area,
                      company.registered_business_address_city,
                    )}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {company.gst_number || "N/A"}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>PO Details</h4>
                  <p>
                    <strong>RFQ Number:</strong> {orderData.rfq_number || "N/A"}
                  </p>
                  <p>
                    <strong>PO Number:</strong> {orderData.po_number}
                  </p>
                  <p>
                    <strong>PO Date:</strong> {formatPoDate(orderData.po_created_date)}
                  </p>
                  <p>
                    <strong>Payment Terms:</strong>{" "}
                    {orderData.payment_terms || "N/A"}
                  </p>
                  <p>
                    <strong>Delivery Date:</strong>{" "}
                    {rfqItems[0]?.delivery_date || "N/A"}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    textAlign: "center",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <img
                    src={company.company_logo || newLogo}
                    alt="Company Logo"
                    style={{
                      height: "80px",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </div>

              <div
                style={{ display: "flex", borderBottom: "1px solid #bcb6b6" }}
              >
                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>Vendor Details</h4>
                  <p>
                    <strong>Name:</strong>{" "}
                    {vendor.person_name || vendor.name || "N/A"}
                  </p>
                  <p>
                    <strong>Mobile:</strong>{" "}
                    {vendor.person_phone || vendor.phone_number || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong>{" "}
                    {vendor.person_email || vendor.email || "N/A"}
                  </p>
                  <p>
                    <strong>Address:</strong>{" "}
                    {formatPoAddress(
                      vendor.registered_business_address_building,
                      vendor.registered_business_address_area,
                      vendor.registered_business_address_city,
                    )}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {vendor.gst_number || "N/A"}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>Billing Details</h3>
                  {billingDetails.billing_keypersons?.length > 0 && (
                      <>
                        <p>
                          <strong>Name:</strong>{" "}
                          {billingDetails.billing_keypersons[0].name || "N/A"}
                        </p>
                        <p>
                          <strong>Mobile:</strong>{" "}
                          {billingDetails.billing_keypersons[0].mobile_number ||
                            "N/A"}
                        </p>
                        <p>
                          <strong>Email:</strong>{" "}
                          {billingDetails.billing_keypersons[0].email || "N/A"}
                        </p>
                      </>
                    )}
                  <p>
                    <strong>Address:</strong>{" "}
                    {formatPoAddress(
                      billingDetails.billing_address_line_1,
                      billingDetails.billing_address_line_2,
                      billingDetails.billing_city,
                      billingDetails.billing_state,
                      billingDetails.billing_pincode,
                    )}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {siteLocation.gst_number || "N/A"}
                  </p>
                </div>

                <div
                  style={{
                    width: "33.33%",
                    padding: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  <h4 style={{ marginTop: 0 }}>Shipping Details</h4>
                  {shippingDetails.shipping_keypersons?.length > 0 && (
                      <>
                        <p>
                          <strong>Name:</strong>{" "}
                          {shippingDetails.shipping_keypersons[0].name || "N/A"}
                        </p>
                        <p>
                          <strong>Mobile:</strong>{" "}
                          {shippingDetails.shipping_keypersons[0]
                            .mobile_number || "N/A"}
                        </p>
                        <p>
                          <strong>Email:</strong>{" "}
                          {shippingDetails.shipping_keypersons[0].email || "N/A"}
                        </p>
                      </>
                    )}
                  <p>
                    <strong>Address:</strong>{" "}
                    {formatPoAddress(
                      shippingDetails.shipping_address_line_1,
                      shippingDetails.shipping_address_line_2,
                      shippingDetails.shipping_city,
                      shippingDetails.shipping_state,
                      shippingDetails.shipping_pincode,
                    )}
                  </p>
                  <p>
                    <strong>GSTN:</strong> {siteLocation.gst_number || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="hltxt" style={{ marginTop: "4px" }}>
              <strong>
                Reference: Vendor Final Offer : {orderData.reference_number || orderData.rfq_number || "N/A"}
                {rfqItems[0]?.delivery_date
                  ? ` | ${rfqItems[0].delivery_date}`
                  : ""}
              </strong>
            </div>
            <Table
              className="poordertbl"
              columns={columns}
              dataSource={data}
              rowKey={(record, index) =>
                `${record.SrNo || "row"}-${record.ItemDescription || index}`
              }
              pagination={false}
              rowClassName={() => "custom-row-height"}
            />

            <div className="invofooter po1">
              <table>
                <tr>
                  <th>
                    <b>Basic Sub Total:</b>
                  </th>
                  <td>
                    <b>
                      ₹{" "}
                      {Number(totals.basic_sub_total || 0).toLocaleString(
                        "en-IN",
                        { maximumFractionDigits: 0 },
                      )}
                    </b>
                  </td>
                </tr>
                <tr>
                  <th>CGST:</th>
                  <td>
                    ₹{" "}
                    {Number(totals.cgst || 0).toLocaleString("en-IN", {
                      maximumFractionDigits: 0,
                    })}
                  </td>
                </tr>
                <tr>
                  <th>SGST:</th>
                  <td>
                    ₹{" "}
                    {Number(totals.sgst || 0).toLocaleString("en-IN", {
                      maximumFractionDigits: 0,
                    })}
                  </td>
                </tr>
                <tr>
                  <th className="ttlbg">
                    <b>Total:</b>
                  </th>
                  <td className="ttlbg">
                    <b>
                      ₹{" "}
                      {Number(totals.total || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })}
                    </b>
                  </td>
                </tr>
              </table>
            </div>
            <div className="hltxt">
              AMOUNT IN WORDS:{" "}
              <b>{convertNumberToWords(Number(totals.total || 0).toFixed(0))}</b>
            </div>
            <Row gutter={[15]}>
              <Col xs={24} sm={24} md={12}>
                <div
                  className="orderpdtl ftrsigntbl"
                  style={{ marginLeft: "12px" }}
                >
                  <h3>
                    <label>Other Terms:</label> The Seller has accepted order
                    unconditionally.
                  </h3>
                  <p>
                    <strong>For, {company.name || "N/A"}</strong>
                  </p>
                  <br />
                  <p className="authsign">Authorised Signatory</p>
                </div>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <div className="orderpdtl ftrsigntbl rightalign">
                  <p style={{ textAlign: "start", marginLeft: "67%" }}>
                    <strong>For, {vendor.name || vendor.person_name || "N/A"}</strong>
                  </p>
                  <br />
                  <p className="authsign">Authorised Signatory</p>
                </div>
              </Col>
            </Row>

            <div className="details-container">
              <div className="details-wrapper">
                {billingDetails.billing_keypersons?.length > 1 && (
                    <div className="key-person-section">
                      <h4 className="section-title">
                        Additional Billing Key Persons
                      </h4>
                      <table className="details-table">
                        <thead>
                          <tr className="table-header">
                            <th>Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billingDetails.billing_keypersons
                            .slice(1)
                            .map((person, index) => (
                              <tr key={index}>
                                <td>{person.name ? person.name : "NA"}</td>
                                <td>{person.email ? person.email : "NA"}</td>
                                <td>
                                  {person.mobile_number
                                    ? person.mobile_number
                                    : "NA"}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                {shippingDetails.shipping_keypersons?.length > 1 && (
                    <div className="key-person-section">
                      <h4 className="section-title">
                        Additional Shipping Key Persons
                      </h4>
                      <table className="details-table">
                        <thead>
                          <tr className="table-header">
                            <th>Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shippingDetails.shipping_keypersons
                            .slice(1)
                            .map((person, index) => (
                              <tr key={index}>
                                <td>{person.name ? person.name : "NA"}</td>
                                <td>{person.email ? person.email : "NA"}</td>
                                <td>
                                  {person.mobile_number
                                    ? person.mobile_number
                                    : "NA"}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

GeneralPurchaseOrder.propTypes = {
  id: PropTypes.number,
  vendorId: PropTypes.number,
};

export default GeneralPurchaseOrder;
